import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged } from 'type-fest';
import type { Utils } from '../../../../shared';
import type { AggregationRoot, MergeableEntity } from '../../common';
import { SystemMutableEntity as SME } from '../../common';
import { UniqueShortId } from '../../common/id';
import { Identifier as PersonIdentifier } from '../../common/identifier';
import type { SystemCommandContext } from '../common';
import type { Event } from '../event';
import { _SessionIdentifier } from './identifier';

import Project = AuthDomain.Object.Project;

const sessionIdGenerator = UniqueShortId.GetFactory(21);

export type Session = AggregationRoot &
  SME &
  MergeableEntity & {
    id: Session.Id;
    projectId: Project.Id;
    identifiers: Session.Identifier[];
    /** 人物識別子（セッション識別子 `identifiers` とは別に蓄積） */
    temporaryUserIdentifiers: PersonIdentifier[];
  };

export namespace Session {
  export import Identifier = _SessionIdentifier;

  export type Id = Tagged<string, 'sessionId'>;
  export namespace Id {
    export const Generate = (): Id => sessionIdGenerator.generate() as unknown as Id;
    export function FromString(id: string): Id {
      // sessionId は troppus 外部から受け取ることもあるので緩めのチェックにするが、
      // 短すぎる ID は衝突や推測のリスクがあるため 8 文字以上を必須とする
      if (id.length < 8 || id.length > 36) {
        throw new Error(`Invalid Session.Id: ${id} (length must be 8..36)`);
      }
      return id as Id;
    }
  }

  export type Props = {
    id: string;
    projectId: string;
    identifiers: _SessionIdentifier.Props[];
    temporaryUserIdentifiers: PersonIdentifier.Props[];
    mergedTo: Session.Id | undefined;
    mergedAt: Utils.Date.dayjs | undefined;
  } & SME.Props;

  export const Construct = (props: Props): Session => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      identifiers: props.identifiers.map(Identifier.Construct),
      temporaryUserIdentifiers: props.temporaryUserIdentifiers.map(PersonIdentifier.Construct),
      mergedTo: undefined,
      mergedAt: undefined,
      ...SME.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Pick<Session.Props, 'identifiers' | 'temporaryUserIdentifiers'> & {
      id: Session.Id | undefined;
    };
    export type Params = {
      context: SystemCommandContext;
      props: Create.Props;
    };
  }

  export const Create = (params: Create.Params): Session => {
    const { context, props } = params;
    const { projectId, now } = context;

    return {
      id: props.id ?? Id.Generate(),
      projectId,
      identifiers: props.identifiers?.map(Identifier.Construct) ?? [],
      temporaryUserIdentifiers: props.temporaryUserIdentifiers?.map(PersonIdentifier.Construct) ?? [],
      ...SME.Create({ date: now }),
      mergedTo: undefined,
      mergedAt: undefined,
    };
  };

  export const MergeSessionsWithEvent = (params: {
    context: SystemCommandContext;
    sessions: Session[];
    event: Pick<Event, 'sessionIdentifiers'> & {
      temporaryUserIdentifiers?: PersonIdentifier[];
    };
  }) => {
    const { context, sessions, event } = params;
    const { projectId, now } = context;

    const oldestSession = sessions.sort((a, b) => a.createdAt.valueOf() - b.createdAt.valueOf())[0];

    const mergedSessionId = oldestSession?.id ?? Id.Generate();

    const identifiers = [
      //
      ...sessions.flatMap((session) => session.identifiers),
      ...event.sessionIdentifiers,
    ];

    const identifierMap = new Map<string, Identifier>(
      identifiers.map((identifier) => [
        Identifier.ToString(identifier), // key
        identifier, // value
      ]),
    );

    const temporaryUserIdentifiers = [
      ...sessions.flatMap((session) => session.temporaryUserIdentifiers),
      ...(event.temporaryUserIdentifiers ?? []),
    ];

    const temporaryUserIdentifierMap = new Map<string, PersonIdentifier>(
      temporaryUserIdentifiers.map((identifier) => [PersonIdentifier.ToString(identifier), identifier]),
    );

    const mutable = oldestSession ? SME.Update({ cur: oldestSession, date: now }) : SME.Create({ date: now });
    const mergedSession = Construct({
      id: mergedSessionId,
      projectId,
      identifiers: Array.from(identifierMap.values()),
      temporaryUserIdentifiers: Array.from(temporaryUserIdentifierMap.values()),
      createdAt: mutable.createdAt,
      updatedAt: mutable.updatedAt,
      mergedTo: undefined,
      mergedAt: undefined,
    });

    return {
      mergedSession,
      sessionsToRemove: sessions.filter((s) => s.id !== mergedSession.id),
    };
  };
}
